import React, { useEffect, useState } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Building2, Download, FileText, Plus, Trash2 } from "lucide-react";
import { useI18n } from "@/i18n";
import { formatDate } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLookupValues } from "@/lib/platformConfig";

const createEmptyInstitutionDocForm = () => ({
  kind: "",
  number: "",
  issue_date: "",
  expiry_date: "",
  notes: "",
  file: null,
});

function normalizeInstitutionDocPayload(form, institutionId) {
  const payload = {
    subject_type: "institution",
    subject_id: institutionId,
    kind: form.kind,
  };
  if (form.number?.trim()) payload.number = form.number.trim();
  if (form.issue_date) payload.issue_date = form.issue_date;
  if (form.expiry_date) payload.expiry_date = form.expiry_date;
  if (form.notes?.trim()) payload.notes = form.notes.trim();
  return payload;
}

function institutionFormValues(institution) {
  return {
    name: institution?.name || "",
    board: institution?.board || "",
    board_affiliation_no: institution?.board_affiliation_no || "",
    contact_email: institution?.contact_email || "",
    contact_phone: institution?.contact_phone || "",
    address_json: institution?.address_json || {},
  };
}

export default function Institution() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const inst = user?.institution;
  const [form, setForm] = useState(() => institutionFormValues(inst));
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [docForm, setDocForm] = useState(createEmptyInstitutionDocForm);
  const qc = useQueryClient();

  const lookup = useLookupValues("institution_document_kinds", { enabled: !!inst?.id });
  const docKinds = (lookup.data?.values || []).map((row) => ({ value: row.value, label: row.label }));

  useEffect(() => {
    if (!inst) return;
    setForm(institutionFormValues(inst));
  }, [inst]);

  useEffect(() => {
    if (!docKinds.length) return;
    setDocForm((prev) => {
      if (docKinds.some((entry) => entry.value === prev.kind)) {
        return prev;
      }
      return { ...prev, kind: docKinds[0].value };
    });
  }, [docKinds]);

  const { data: docs = [], isLoading: docsLoading } = useQuery({
    queryKey: ["institution-documents", inst?.id],
    queryFn: async () =>
      unwrap(
        await api.get("/documents", {
          params: { subject_type: "institution", subject_id: inst.id },
        }),
      ),
    enabled: !!inst?.id,
  });

  const mut = useMutation({
    mutationFn: async () => unwrap(await api.patch(`/institutions/${inst.id}`, form)),
    onSuccess: () => {
      toast.success("Institution updated");
      refresh();
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const isDirty = JSON.stringify(form) !== JSON.stringify(institutionFormValues(inst));

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const uploaded = unwrap(await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }));
    return uploaded.id;
  };

  const createDocMut = useMutation({
    mutationFn: async () => {
      const payload = normalizeInstitutionDocPayload(docForm, inst.id);
      if (docForm.file) {
        payload.file_id = await uploadFile(docForm.file);
      }
      return unwrap(await api.post("/documents", payload));
    },
    onSuccess: () => {
      toast.success("Document uploaded");
      qc.invalidateQueries({ queryKey: ["institution-documents", inst?.id] });
      qc.invalidateQueries({ queryKey: ["documents-search"] });
      qc.invalidateQueries({ queryKey: ["docs-expiring"] });
      setDocForm(createEmptyInstitutionDocForm());
      setDocDialogOpen(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const deleteDocMut = useMutation({
    mutationFn: async (docId) => unwrap(await api.delete(`/documents/${docId}`)),
    onSuccess: () => {
      toast.success("Document deleted");
      qc.invalidateQueries({ queryKey: ["institution-documents", inst?.id] });
      qc.invalidateQueries({ queryKey: ["documents-search"] });
      qc.invalidateQueries({ queryKey: ["docs-expiring"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const kindLabel = (kind) => docKinds.find((entry) => entry.value === kind)?.label || kind;

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name || `${kindLabel(doc.kind)}.bin`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!inst) return <Skeleton className="h-96" />;

  return (
    <div className="p-6 lg:p-8 max-w-5xl space-y-6" data-testid="institution-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">{t("management.institutionTitle")}</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">{t("management.institutionSubtitle")}</p>
      </div>

      <Card className="border-[var(--subtle)]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display">
            <Building2 className="h-4 w-4 text-[var(--accent)]" /> {inst.institution_type} · {inst.board?.toUpperCase()}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}
            className="space-y-4"
          >
            <div>
              <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="inst-name" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">Board / regulator</Label>
                <Input value={form.board} onChange={(e) => setForm({ ...form, board: e.target.value })} className="mt-1" data-testid="inst-board" />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">Board affiliation no.</Label>
                <Input value={form.board_affiliation_no} onChange={(e) => setForm({ ...form, board_affiliation_no: e.target.value })} className="mt-1" data-testid="inst-affiliation" />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">Contact phone</Label>
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="mt-1" data-testid="inst-phone" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">Contact email</Label>
              <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="mt-1" data-testid="inst-email" />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">Address</Label>
              <Input value={form.address_json?.line1 || ""} onChange={(e) => setForm({ ...form, address_json: { ...form.address_json, line1: e.target.value } })} className="mt-1" placeholder="Street address" data-testid="inst-address" />
            </div>
            <div className="flex justify-end">
              {isDirty && (
                <Button
                  type="submit"
                  className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                  disabled={mut.isPending}
                  data-testid="save-institution"
                >
                  {mut.isPending ? "Saving..." : "Save changes"}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-[var(--subtle)]" data-testid="institution-document-upload-section">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 font-display">
            <FileText className="h-4 w-4 text-[var(--accent)]" /> Document upload
          </CardTitle>
          <Button
            type="button"
            className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
            onClick={() => setDocDialogOpen(true)}
            disabled={!docKinds.length}
            data-testid="institution-add-document-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add document
          </Button>
        </CardHeader>
        <CardContent>
          {!docKinds.length && (
            <p className="text-xs text-[var(--text-muted)] mb-3">
              No institution document types configured in Platform Settings.
            </p>
          )}

          {docsLoading ? (
            <Skeleton className="h-32" />
          ) : docs.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">No institution documents uploaded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm grid-table">
                <thead>
                  <tr>
                    <th className="text-left px-3 py-2.5">Document type</th>
                    <th className="text-left px-3 py-2.5">Number</th>
                    <th className="text-left px-3 py-2.5">Issue date</th>
                    <th className="text-left px-3 py-2.5">Expiry date</th>
                    <th className="text-left px-3 py-2.5">File</th>
                    <th className="text-right px-3 py-2.5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((doc) => (
                    <tr key={doc.id} data-testid={`institution-doc-row-${doc.id}`}>
                      <td className="px-3 py-2.5">{kindLabel(doc.kind)}</td>
                      <td className="px-3 py-2.5 font-mono-num text-xs">{doc.number || "-"}</td>
                      <td className="px-3 py-2.5 font-mono-num text-xs">{formatDate(doc.issue_date)}</td>
                      <td className="px-3 py-2.5 font-mono-num text-xs">{formatDate(doc.expiry_date)}</td>
                      <td className="px-3 py-2.5 text-xs text-[var(--text-muted)]">{doc.file_name || "No file"}</td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="inline-flex items-center gap-1">
                          {doc.file_id && (
                            <Button variant="ghost" size="sm" onClick={() => downloadDoc(doc)} data-testid={`institution-doc-download-${doc.id}`}>
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => deleteDocMut.mutate(doc.id)} data-testid={`institution-doc-delete-${doc.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={docDialogOpen} onOpenChange={setDocDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Add Document - {inst.name}</DialogTitle>
            <DialogDescription>
              Upload institution compliance documents with type, number, dates, and notes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className="text-xs uppercase font-semibold">Document type</Label>
                <Select value={docForm.kind} onValueChange={(value) => setDocForm((prev) => ({ ...prev, kind: value }))}>
                  <SelectTrigger className="mt-1" data-testid="institution-document-type">
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {docKinds.map((entry) => (
                      <SelectItem key={entry.value} value={entry.value}>
                        {entry.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs uppercase font-semibold">Upload document</Label>
                <Input
                  type="file"
                  className="mt-1"
                  accept="application/pdf,image/jpeg,image/png"
                  onChange={(e) => setDocForm((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
                  data-testid="institution-document-file"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase font-semibold">Document number</Label>
              <Input
                value={docForm.number}
                onChange={(e) => setDocForm((prev) => ({ ...prev, number: e.target.value }))}
                className="mt-1"
                data-testid="institution-document-number"
              />
            </div>

            <div>
              <Label className="text-xs uppercase font-semibold">Issue date</Label>
              <Input
                type="date"
                value={docForm.issue_date}
                onChange={(e) => setDocForm((prev) => ({ ...prev, issue_date: e.target.value }))}
                className="mt-1"
                data-testid="institution-document-issue-date"
              />
            </div>

            <div>
              <Label className="text-xs uppercase font-semibold">Expiry date</Label>
              <Input
                type="date"
                value={docForm.expiry_date}
                onChange={(e) => setDocForm((prev) => ({ ...prev, expiry_date: e.target.value }))}
                className="mt-1"
                data-testid="institution-document-expiry-date"
              />
            </div>

            <div>
              <Label className="text-xs uppercase font-semibold">Notes</Label>
              <Input
                value={docForm.notes}
                onChange={(e) => setDocForm((prev) => ({ ...prev, notes: e.target.value }))}
                className="mt-1"
                data-testid="institution-document-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              onClick={() => createDocMut.mutate()}
              disabled={!docForm.kind || !docForm.file || createDocMut.isPending}
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
              data-testid="institution-document-save"
            >
              {createDocMut.isPending ? "Saving..." : "Save document"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
